import { getModelMapper } from '../../core/functional/ModelMapper';

/**
 * The cross-app auth payload.
 *
 * `oldResponse` is kept as a nested shape because the login and
 * register-and-complete responses still carry it, and the reducer state (and the
 * sessionStorage copy of it) is typed on this. The handle exchange returns the
 * same fields flat, and `AuthDataFromExchange` below adapts them.
 */
export type ServerAuthData = {
  data: {
    oldResponse: {
      token: string;
    };
    token: string;
    handle?: string;
  };
};

export type AuthData = {
  oldResponse: {
    token: string;
  };
  token: string;
  /** Present on a fresh login/registration; used to hand the session onward. */
  handle?: string;
};

/**
 * What POST /v1/auth/exchange answers with - flat and explicitly named, rather
 * than the old nested blob. The nesting is what let the entire upstream user row
 * travel along, so the new contract names each field it carries.
 */
export type ExchangePayload = {
  token: string;
  legacy_token?: string;
  rc_token?: string;
  rc_user_id?: string;
  role?: string;
  version?: number;
  is_fully_allowed?: boolean;
  user?: {
    id?: number;
    first_name?: string;
    last_name?: string;
    artisan_role?: string;
  };
};

/**
 * Adapt an exchange response onto the shape the reducer already stores.
 *
 * Returns undefined when the payload cannot produce a usable session. The legacy
 * token is what getUser() authenticates with, so casting an absent one to string
 * only moved the failure: getUser(undefined) answers 401, the profile never
 * arrives, and ProfilingForm's submit button silently does nothing because `user`
 * is undefined.
 *
 * An absent legacy token is an EDGE CASE, not a normal path: POST /v1/auth/handle
 * reads it from Django's authtoken_token for every handle it mints, so it is only
 * missing for a user with no row in that table. Guarded anyway, because the field
 * is optional in the contract and the failure mode is silent.
 */
export const authDataFromExchange = (
  payload: ExchangePayload
): Optional<AuthData> => {
  if (!payload.token || !payload.legacy_token) {
    return undefined;
  }

  return {
    token: payload.token,
    oldResponse: { token: payload.legacy_token },
  };
};

export const AuthDataMapper = getModelMapper<AuthData, ServerAuthData>(
  model => model.data,
  model => ({ data: model })
);
